import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { MockUpUtil } from '@xengage/gw-portals-util-js';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import './PolicyInfo.messages';
import { screenReaderAnnounce, validationUtil } from 'premier-capability-hooks';
import presentationMetadata from './PolicyInfoPage.metadata.json5';
import { TextInput } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function removeInitialData(submissionVM) {
    // eslint-disable-next-line no-param-reassign
    submissionVM.value = MockUpUtil.cleanUpMockedProperties(
        _.get(submissionVM, 'value'),
        'quote.bop',
        'bindData.contactPhone',
        'bindData.contactEmail',
        'baseData.accountHolder.emailAddress1'
    );

    return submissionVM;
}

function compareAddresses(policyAddress, billingAddress) {
    if (billingAddress?.addressLine1) {
        const lineValid =
            policyAddress.addressLine1.value ===
            billingAddress.addressLine1.value;
        const cityValid =
            policyAddress.city.value === billingAddress.city.value;
        const stateValid =
            policyAddress.state.value === billingAddress.state.value;
        const postcodeValid =
            policyAddress.postalCode.value === billingAddress.postalCode.value;

        return lineValid && cityValid && stateValid && postcodeValid;
    }

    return true;
}

function PolicyInfoPage(props) {
    const { authHeader } = useAuthentication();
    const [isVMFormValid, setVMFormValid] = useState(false);
    const [isVmInitialized, updateIsVmInitialized] = useState(false);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { wizardData: submissionVM, updateWizardData, currentStep } = props;
    const isDirectBilling = submissionVM.value.baseData.billingType_EY === 'DirectBill'
    const [isBillingSame, updateIsBillingSame] = useState(true);
    const { pageKey } = currentStep;
    // eslint-disable-next-line security/detect-object-injection
    const metadata = presentationMetadata[pageKey];

    const breakpoint = useContext(BreakpointTrackerContext);

    const periodStartDate = _.get(submissionVM.value, 'baseData.periodStartDate');

    useEffect(() => {
        if (!isVmInitialized) {
            removeInitialData(submissionVM);
            updateIsVmInitialized(true);
        }

        if (
            submissionVM &&
            submissionVM.bindData &&
            submissionVM.baseData &&
            submissionVM.baseData.accountHolder
        ) {
            const { primaryAddress } = submissionVM.baseData.accountHolder;
            const { billingAddress } = submissionVM.bindData;
            const isBillingEqualToPrimary = compareAddresses(
                primaryAddress,
                billingAddress
            );

            updateIsBillingSame(isBillingEqualToPrimary);

        }
    }, [updateIsVmInitialized, submissionVM, isVmInitialized]);

    const setPrimaryAddress = useCallback(
        value => {
            const primaryCountry =
                submissionVM.baseData.accountHolder.primaryAddress.value
                    .country;
            const address = value
                ? submissionVM.baseData.accountHolder.primaryAddress.value
                : { country: primaryCountry };

            const addressP = isDirectBilling ? address : undefined

            _.set(submissionVM, 'bindData.billingAddress.value', addressP);

            const valueP = isDirectBilling ? value : false

            updateIsBillingSame(valueP);
        },
        [isDirectBilling, submissionVM]
    );

    const onNext = async () => {
        submissionVM.value =
            await LoadSaveService.updateQuotedSubmissionWithLOBData(
                submissionVM.value,
                authHeader
            );

        return submissionVM;
    };

    const onSave = useCallback(
        async () => {
            const subVMCopy = _.clone(submissionVM);

            validationUtil.unSetIsValidationReqFlag(subVMCopy.bindData.value, ' ')

            await LoadSaveService.updateQuotedSubmissionWithLOBData(
                subVMCopy.value,
                authHeader
            );

            return submissionVM;
        },
        [LoadSaveService, authHeader, submissionVM]
    );

    const onPrevious = async () => {
        const contactPhone = _.get(submissionVM, 'value.bindData.contactPhone');

        if (_.isNil(contactPhone) || contactPhone.indexOf('_') !== -1) {
            // MockUpUtil won't set a mockValue correctly if the VMValue is falsy for _.isEmpty
            // So we set it as undefined to ensure the mockValue is set correctly
            _.set(submissionVM, 'value.bindData.contactPhone', undefined);
            submissionVM.value = MockUpUtil.setMockData(
                submissionVM.value,
                'quote.PremierLE',
                'bindData.contactPhone'
            );
        }

        return submissionVM;
    };

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showRequired:true
        },
        isBillingSame: {
            value: isDirectBilling ? isBillingSame : false,
            onValueChange: setPrimaryAddress
        },
        billingAddressLine1: {
            visible: isDirectBilling && !isBillingSame,
            required: isDirectBilling && !isBillingSame
        },
        billingAddressLine2: {
            visible: isDirectBilling && !isBillingSame,
        },
        billingAddressLine3: {
            visible: isDirectBilling && !isBillingSame,
        },
        billingCity: {
            visible: isDirectBilling && !isBillingSame,
            required: isDirectBilling && !isBillingSame
        },
        billingState: {
            visible: isDirectBilling && !isBillingSame,
            required: isDirectBilling && !isBillingSame
        },
        billingPostalCode: {
            visible: isDirectBilling && !isBillingSame,
            required: isDirectBilling && !isBillingSame
        },
        policyPeriod: {
            value: new Date(periodStartDate.year, periodStartDate.month, periodStartDate.day).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: '2-digit' })
        },
    };

    const resolvers = {
        resolveCallbackMap: {
            updateIsBillingSame,
        },
        resolveComponentMap: {
            TextInput
        }
    };

    const onModelChange = value => {
        if(!_.isUndefined(value.isBillingSame) && value.isBillingSame && !isDirectBilling) {
            // eslint-disable-next-line no-param-reassign
            value.isBillingSame = false
        }
        
        updateWizardData(value);
    };

    const onValidationChange = () => {
        setVMFormValid(
            submissionVM.aspects.valid && submissionVM.aspects.subtreeValid
        );
    };

    /*
     * The usage of non-VM field within this page
     * necessitated the implementation of readViewModel value.
     *
     * The code reads in the metadata, VM, fieldID, path and overrideProperties,
     * applies some logic to combine where needed and responds with a new template.
     *
     * The combination of this,
     * along with defining the specific value within the override properties,
     * allows us to utilise a non VM field within ViewModelForm.
     */
    const readValue = useCallback(
        (id, path) => readViewModelValue(
                metadata,
                submissionVM,
                id,
                path,
                overrideProps
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [submissionVM, overrideProps]
    );

    return (
        <WizardPage
            skipWhen={() => isVMFormValid}
            disableNext={!isVMFormValid}
            onNext={onNext}
            onSave={onSave}
            onPrevious={onPrevious}
        >
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={onModelChange}
                onValidationChange={onValidationChange}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                resolveValue={readValue}
            />
        </WizardPage>
    );
}

PolicyInfoPage.propTypes = wizardProps;
export default PolicyInfoPage;
